/**
 * Bank statement parsing.
 *
 * A receipt is one payment; a statement is a table of dozens. Maybank2u hands
 * out monthly statements as PDF, not CSV, so this reads the text of one and
 * finds the transaction rows in it.
 *
 * The honest caveat, stated here because it shapes every decision below: there
 * is no published schema for these documents, the layout differs between banks
 * and between years, and this parser has never seen a real Maybank statement --
 * only invented ones written to plausible shapes. So it is built to be
 * *checkable* rather than clever:
 *
 *   - a row is only a row if it has a date and a readable amount;
 *   - the description is whatever sits between them, cleaned up;
 *   - every row carries a confidence and the raw line it came from, so the
 *     import screen can show its working;
 *   - nothing is ever saved without the person seeing the table first.
 *
 * If the numbers look wrong on screen, the parser has failed visibly, which is
 * the only acceptable way for it to fail.
 */
import { parseAmountToSen } from '../money.js';
import { DEFAULT_TIME_ZONE } from '../time.js';
import type { TransactionType } from '../schemas.js';
import { datePartsToIso, normalizeReceiptText, parseReceiptDate, type ParseOptions } from './common.js';

export interface StatementRow {
  /** Position in the document, so the preview can keep the original order. */
  index: number;
  occurredAt: string;
  description: string;
  amountSen: number;
  type: TransactionType;
  /** Running balance, when the statement has a balance column. */
  balanceSen?: number;
  confidence: number;
  /** The line this came from, shown in the preview so the person can check it. */
  raw: string;
}

export interface StatementParseResult {
  rows: StatementRow[];
  /** Lines that looked like they might be transactions but could not be read. */
  skipped: { line: string; reason: string }[];
  /** What the parser thinks it is looking at. */
  issuer: 'maybank' | 'tng' | 'unknown';
  warnings: string[];
}

const ISSUER_SIGNATURES: { issuer: 'maybank' | 'tng'; needles: string[] }[] = [
  {
    issuer: 'maybank',
    needles: ['maybank', 'maybank2u', 'mae by maybank', 'malayan banking', 'm2u'],
  },
  { issuer: 'tng', needles: ["touch 'n go", 'touch n go', 'tng ewallet', 'tng digital'] },
];

function detectIssuer(text: string): 'maybank' | 'tng' | 'unknown' {
  const lower = text.toLowerCase();
  for (const entry of ISSUER_SIGNATURES) {
    if (entry.needles.some((needle) => lower.includes(needle))) return entry.issuer;
  }
  return 'unknown';
}

/** Every money-shaped token on a line, with where it sat. */
interface AmountToken {
  sen: number;
  /** A trailing or leading sign that the statement used to mark direction. */
  sign: '+' | '-' | null;
  start: number;
  end: number;
}

const AMOUNT_PATTERN =
  /(?<lead>[-+])?\s*(?:RM|MYR)?\s*(?<body>\d{1,3}(?:,\d{3})*(?:\.\d{2})|\d+\.\d{2})\s*(?<trail>[-+]|CR|DR)?/gi;

function findAmountTokens(line: string): AmountToken[] {
  const tokens: AmountToken[] = [];
  AMOUNT_PATTERN.lastIndex = 0;

  let match = AMOUNT_PATTERN.exec(line);
  while (match) {
    const body = match.groups?.body;
    if (body) {
      try {
        const sen = parseAmountToSen(body);
        const lead = match.groups?.lead;
        const trail = match.groups?.trail?.toUpperCase();
        const sign: '+' | '-' | null =
          lead === '-' || trail === '-' || trail === 'DR'
            ? '-'
            : lead === '+' || trail === '+' || trail === 'CR'
              ? '+'
              : null;
        tokens.push({ sen, sign, start: match.index, end: match.index + match[0].length });
      } catch {
        // Not a readable amount; a row without one is not a row.
      }
    }
    match = AMOUNT_PATTERN.exec(line);
  }
  return tokens;
}

/** Words that mark a line as a credit even without a sign column. */
const CREDIT_WORDS = [
  'salary',
  'gaji',
  'credit',
  'kredit',
  'deposit',
  'refund',
  'reversal',
  'cashback',
  'dividend',
  'interest',
  'incoming',
  'received',
];

const TRANSFER_WORDS = [
  'transfer',
  'pindahan',
  'own account',
  'reload',
  'top up',
  'topup',
  'ibg',
  'duitnow transfer',
  'credit card payment',
];

/** Lines that are page furniture, not transactions. */
const NOISE_PATTERNS = [
  /^page\s+\d+/i,
  /opening balance/i,
  /closing balance/i,
  /beginning balance/i,
  /ending balance/i,
  /^balance b\/f/i,
  /^balance c\/f/i,
  /total\s+(debit|credit|transactions)/i,
  /statement (period|date|of account)/i,
  /account (number|no|holder)/i,
  /^date\s+(description|transaction|details)/i,
  /penyata|baki akhir/i,
];

const isNoise = (line: string): boolean => NOISE_PATTERNS.some((pattern) => pattern.test(line));

function classify(description: string, sign: '+' | '-' | null): {
  type: TransactionType;
  confidence: number;
} {
  const lower = description.toLowerCase();

  if (TRANSFER_WORDS.some((word) => lower.includes(word))) {
    return { type: 'transfer', confidence: 0.85 };
  }
  if (sign === '+') {
    const isRefund = /refund|reversal|cashback/i.test(lower);
    return { type: isRefund ? 'refund' : 'income', confidence: 0.85 };
  }
  if (CREDIT_WORDS.some((word) => lower.includes(word))) {
    return { type: /refund|reversal|cashback/i.test(lower) ? 'refund' : 'income', confidence: 0.7 };
  }
  // A statement line with no credit marker is money going out.
  return { type: 'expense', confidence: sign === '-' ? 0.9 : 0.75 };
}

function cleanDescription(value: string): string {
  return value
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s|:.-]+|[\s|:.-]+$/g, '')
    .slice(0, 120)
    .trim();
}

export interface StatementParseOptions extends ParseOptions {
  /**
   * Set when the statement has a running-balance column. Auto-detected by
   * default: a document where most rows end with two amounts has one.
   */
  hasBalanceColumn?: boolean;
}

/**
 * Read transaction rows out of statement text.
 *
 * The shape it looks for is the one every statement shares, whatever the
 * layout: a date, some description, and one or two amounts, where the last
 * amount is the running balance if the document has a balance column.
 */
export function parseStatementText(
  raw: string,
  options: StatementParseOptions = {},
): StatementParseResult {
  const timeZone = options.timeZone ?? DEFAULT_TIME_ZONE;
  const lines = normalizeReceiptText(raw);
  const issuer = detectIssuer(raw);
  const warnings: string[] = [];
  const skipped: { line: string; reason: string }[] = [];

  // A candidate is any line that opens with a date and carries an amount.
  interface Candidate {
    line: string;
    lineIndex: number;
    dateIso: string;
    dateConfidence: number;
    amounts: AmountToken[];
    dateEnd: number;
  }

  const candidates: Candidate[] = [];

  lines.forEach((line, lineIndex) => {
    if (isNoise(line)) return;

    const amounts = findAmountTokens(line);
    if (amounts.length === 0) return;

    // The date has to be at the start of the row, otherwise a reference number
    // in the middle of a description starts looking like one.
    const head = line.slice(0, 24);
    const parsedDate = parseReceiptDate(head, options);
    if (!parsedDate) {
      if (/\d{1,2}[/-]\d{1,2}/.test(head)) {
        skipped.push({ line, reason: 'The date at the start of this row could not be read.' });
      }
      return;
    }

    const dateMatch = /\d{1,2}[\s/-][A-Za-z0-9]{2,9}[\s/-]?\d{0,4}|\d{4}-\d{2}-\d{2}/.exec(head);

    candidates.push({
      line,
      lineIndex,
      dateIso: datePartsToIso(parsedDate.parts, timeZone),
      dateConfidence: parsedDate.confidence,
      amounts,
      dateEnd: (dateMatch?.index ?? 0) + (dateMatch?.[0].length ?? 0),
    });
  });

  if (candidates.length === 0) {
    return {
      rows: [],
      skipped,
      issuer,
      warnings: [
        'No transaction rows were found. If this is a scanned statement rather than a text one, there is no text to read -- open it on your iPhone, select the text with Live Text, and paste it instead.',
      ],
    };
  }

  // A balance column shows up as most rows carrying two amounts.
  const withTwo = candidates.filter((candidate) => candidate.amounts.length >= 2).length;
  const hasBalance = options.hasBalanceColumn ?? withTwo > candidates.length / 2;

  if (options.hasBalanceColumn === undefined && withTwo > 0 && !hasBalance) {
    warnings.push(
      'Some rows have two amounts and some have one, so the balance column could not be identified with confidence. Check the amounts below before importing.',
    );
  }

  const rows: StatementRow[] = candidates.map((candidate, index) => {
    // With a balance column the transaction amount is the second-to-last
    // number on the line; without one it is the last.
    const amountToken =
      hasBalance && candidate.amounts.length >= 2
        ? candidate.amounts[candidate.amounts.length - 2]
        : candidate.amounts[candidate.amounts.length - 1];
    const balanceToken =
      hasBalance && candidate.amounts.length >= 2
        ? candidate.amounts[candidate.amounts.length - 1]
        : undefined;

    const amount = amountToken as AmountToken;
    const description = cleanDescription(candidate.line.slice(candidate.dateEnd, amount.start));
    const { type, confidence: typeConfidence } = classify(description, amount.sign);

    const confidence =
      Math.round(
        Math.min(
          candidate.dateConfidence,
          typeConfidence,
          description.length >= 3 ? 0.9 : 0.5,
          // An unrecognised issuer means the column layout is a guess.
          issuer === 'unknown' ? 0.6 : 0.9,
        ) * 100,
      ) / 100;

    return {
      index,
      occurredAt: candidate.dateIso,
      description: description || 'No description',
      amountSen: Math.abs(amount.sen),
      type,
      ...(balanceToken ? { balanceSen: balanceToken.sen } : {}),
      confidence,
      raw: candidate.line,
    };
  });

  if (issuer === 'unknown') {
    warnings.push(
      'This does not look like a Maybank or Touch ’n Go statement, so the columns were worked out from the layout alone. Check the amounts carefully.',
    );
  }

  return { rows, skipped, issuer, warnings };
}

/**
 * A running-balance cross-check.
 *
 * When the statement has a balance column, consecutive balances must differ by
 * exactly the transaction amount. That is a genuine arithmetic proof that the
 * right column was read as the amount and the direction was right -- far
 * stronger than any heuristic, and it costs one subtraction per row.
 */
export interface BalanceCheck {
  checked: number;
  mismatches: { index: number; expectedSen: number; actualSen: number }[];
  /** True when every consecutive pair agrees. */
  consistent: boolean;
}

export function checkRunningBalance(rows: readonly StatementRow[]): BalanceCheck {
  const mismatches: BalanceCheck['mismatches'] = [];
  let checked = 0;

  for (let index = 1; index < rows.length; index += 1) {
    const previous = rows[index - 1];
    const current = rows[index];
    if (previous?.balanceSen === undefined || current?.balanceSen === undefined) continue;

    checked += 1;
    const direction = current.type === 'expense' || current.type === 'transfer' ? -1 : 1;
    const expected = previous.balanceSen + direction * current.amountSen;
    if (expected !== current.balanceSen) {
      mismatches.push({
        index: current.index,
        expectedSen: expected,
        actualSen: current.balanceSen,
      });
    }
  }

  return { checked, mismatches, consistent: checked > 0 && mismatches.length === 0 };
}

/**
 * Flip the direction of rows the balance column proves wrong.
 *
 * Statements vary in how they mark a credit; when the running balance says a
 * row went the other way, the balance is right and the heuristic was wrong.
 */
export function reconcileWithBalance(rows: readonly StatementRow[]): StatementRow[] {
  const corrected = rows.map((row) => ({ ...row }));

  for (let index = 1; index < corrected.length; index += 1) {
    const previous = corrected[index - 1];
    const current = corrected[index];
    if (previous?.balanceSen === undefined || current?.balanceSen === undefined) continue;

    const delta = current.balanceSen - previous.balanceSen;
    if (Math.abs(delta) !== current.amountSen) continue;

    const shouldBeCredit = delta > 0;
    const isCredit = current.type === 'income' || current.type === 'refund';

    if (shouldBeCredit !== isCredit) {
      current.type = shouldBeCredit
        ? /refund|reversal|cashback/i.test(current.description)
          ? 'refund'
          : 'income'
        : 'expense';
      // The balance is arithmetic, not a guess, so this is now certain.
      current.confidence = Math.max(current.confidence, 0.95);
    } else {
      current.confidence = Math.max(current.confidence, 0.95);
    }
  }

  return corrected;
}
